/**
 * Soft-safe Copy payload builders (arch Move 5).
 * Sits next to shareViewModel so human Copy cannot diverge from Share UI/PNG
 * story (Soft #2 line → holes → board, hide empties).
 *
 * Soft-refuse: no GTO / % / outs / win rates / FAQ fill / empty-layer em-dash.
 * Quiet edit / toast / SoftGate chrome never enter the summary payload.
 * Soft 14 ops-only: no crawl locale fill or fake locales in Copy.
 */
import { t } from '../i18n/strings'
import { formatEndingLine } from './endingRecord'
import { buildProNotation } from './notation'
import {
  formatCard,
  formatCards,
  formatHolePrettyLine,
  formatSeatCode,
  handBlindsText,
  handLineText,
  handStackText,
  listKnownHoleEntries,
} from './poker'
import { formatPotDisplay } from './potLabel'
import { buildShareViewModel } from './shareViewModel'
import type { HandState } from './types'

export type CopyMode = 'both' | 'human' | 'pro'

/** Localized human-readable summary (no pro block). Discord/Kakao-friendly. */
export function buildHumanSummary(state: HandState): string {
  const ui = t()
  // Soft-safe Move 1+5: one view-model drives Copy layer presence + Soft #2 order.
  const svm = buildShareViewModel(state)
  const blinds = handBlindsText(state)
  const flop = formatCards(state.board.flop)
  const turn = state.board.turn ? formatCard(state.board.turn) : ''
  const river = state.board.river ? formatCard(state.board.river) : ''
  const boardParts = [flop, turn, river].filter(Boolean)
  // Soft empty-layer: omit board when empty — never fabricate "—".
  const board = boardParts.length ? boardParts.join(' | ') : ''

  const holeEntries = listKnownHoleEntries(state, { includeEmptyHero: false })
  const villainKnown = holeEntries.some((e) => !e.isHero)
  const holeLines: string[] = []
  if (svm.layers.holes) {
    if (!villainKnown) {
      const heroLine = formatHolePrettyLine(state.holeCards)
      if (heroLine) holeLines.push(ui.summaryHoleLine(heroLine))
    } else {
      holeLines.push(ui.summaryHolesHeader)
      for (const e of holeEntries) {
        const cards = formatHolePrettyLine(e.hole)
        if (!cards) continue
        holeLines.push(ui.summaryKnownHoleLine(e.label, cards))
      }
    }
  }

  const lines: string[] = [
    ui.summaryHeading,
  ]
  // SoftGate force-share honesty: Copy must carry the same Incomplete stamp
  // as Share UI / PNG — never look "complete" after confirm-force.
  // Soft-refuse: badge only (no solver-metric Soft branding).
  if (svm.incomplete) {
    lines.push(ui.incompleteBadge)
  }
  lines.push(
    '',
    ui.summaryPlayers(state.playerCount),
    ui.summaryBlinds(blinds),
  )
  lines.push(
    ui.summaryHero(formatSeatCode(state.heroPosition, state.playerCount)),
    ui.summaryStack(handStackText(state)),
  )
  if (
    state.effectiveStackBb != null &&
    Number.isFinite(state.effectiveStackBb)
  ) {
    lines.push(ui.summaryEff(state.effectiveStackBb))
  }
  // Softwise M10d: pot amount only — the HU / multiway label is potShape's.
  const potDisp = formatPotDisplay(state.potBb, ui)
  if (potDisp.combinedL1) lines.push(potDisp.combinedL1)

  // Soft #2 order on human copy: line → holes → board (filled layers only).
  // No toast / SoftGate / edit chrome / em-dash fabrication in payload.
  for (const layer of svm.storyOrder) {
    if (layer === 'line') {
      // Softwise M10b: the single line generator (same as Share card / input).
      // Soft Quiet C1: denser street chronology stays UI-only (processMore) — not Copy.
      lines.push('', ui.summaryActionsHeader, handLineText(state))
    } else if (layer === 'holes') {
      if (holeLines.length) lines.push('', ...holeLines)
    } else if (layer === 'board') {
      if (board) lines.push('', ui.summaryBoardLine(board))
    } else if (layer === 'takeaway' && svm.takeaway) {
      lines.push('', ui.takeawayLabel, svm.takeaway)
    }
  }

  // Soft G: optional ending — ONE line right after the last process line
  // (never the first line / meta block). Empty → nothing (byte-identical).
  const endingLine = formatEndingLine(state, ui)
  if (endingLine) lines.push(endingLine)

  lines.push('', ui.summaryFooter)
  return lines.join('\n')
}

export function buildTextSummary(state: HandState): string {
  const ui = t()
  return [
    buildHumanSummary(state),
    '',
    ui.summaryProHeader,
    buildProNotation(state),
  ].join('\n')
}

/** One-tap copy payload for human / pro / both. */
export function buildCopyPayload(
  state: HandState,
  mode: CopyMode = 'both',
): string {
  if (mode === 'human') return buildHumanSummary(state)
  if (mode === 'pro') return buildProNotation(state)
  return buildTextSummary(state)
}
